import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material'
import { Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material'

/**
 * Предел высоты картинки: 90% окна, но не выше, чем помещается в диалог
 * (у бумаги диалога отступ 32px сверху и снизу), за вычетом шапки, рамок
 * ленты и строки со ссылкой — так картинка целиком видна без прокрутки.
 */
const IMAGE_MAX_HEIGHT = 'calc(min(90vh, 100vh - 64px) - 120px)'

interface ChatImageViewerDialogProps {
  open: boolean
  /** Полный адрес картинки (публичный диск API). */
  src: string
  /** Имя файла вложения — заголовок диалога и alt картинки. */
  name: string
  onClose: () => void
}

/**
 * Просмотр картинки из сообщения чата: картинка целиком, насколько позволяет
 * окно, и ссылка на оригинал в новой вкладке. Кнопки «Скачать» нет: атрибут
 * download браузеры не выполняют для файлов с другого домена (api.easya.ru).
 */
export function ChatImageViewerDialog({ open, src, name, onClose }: ChatImageViewerDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      {/* Содержимое размонтируется после закрытия: признак ошибки загрузки
          сбрасывается сам, и следующее открытие пробует картинку заново */}
      <ChatImageViewerContent src={src} name={name} onClose={onClose} />
    </Dialog>
  )
}

function ChatImageViewerContent({ src, name, onClose }: Omit<ChatImageViewerDialogProps, 'open'>) {
  const { t } = useTranslation()
  const [loadFailed, setLoadFailed] = useState(false)
  const title = name.trim() || t('chat.imagePreview')

  return (
    <>
      <DialogTitle
        sx={{ pr: 7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {title}
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Подложка отделяет картинку от бумаги диалога: белый скриншот
          // или прозрачный PNG иначе сливаются с фоном
          bgcolor: 'action.hover',
        }}
      >
        {loadFailed ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {t('chat.imageLoadFailed')}
          </Alert>
        ) : (
          <Box
            component="img"
            src={src}
            alt={title}
            onError={() => setLoadFailed(true)}
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: IMAGE_MAX_HEIGHT,
              objectFit: 'contain',
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          component="a"
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
        >
          {t('chat.openInNewTab')}
        </Button>
      </DialogActions>
    </>
  )
}
